import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { useMutation, useQuery, useSubscription } from "@apollo/react-hooks";
import {
  addDays,
  differenceInMinutes,
  format,
  getWeek,
  isAfter,
  isBefore,
  parseISO,
  subDays,
  endOfDay,
} from "date-fns";
import { Locale } from "date-fns";
import enLocale from "date-fns/locale/en-US";
import huLocale from "date-fns/locale/hu";
import {
  MY_TIMESHEET_CREATE_TIMESHEET,
  MY_TIMESHEET_GET_TIMESHEET_LIST,
  MY_TIMESHEET_REMOVE_TIMESHEET,
  MY_TIMESHEET_TIMESHEETS_SUBSCRIPTION,
  MY_TIMESHEET_TIMESHEET_LIST,
  MY_TIMESHEET_UPDATE_TIMESHEET,
} from "my-timesheet/graphql";
import { sumTimeStrings, toDate, toISO } from "my-timesheet/utils";
import { useOcSnackbar } from "oc/hooks";
import { RoleFilterInput } from "oc/types";
import { v4 as uuid } from "uuid";

type TypeFilters = {
  project: string[];
  client: string[];
  cost: string[];
};

interface UseTimesheetProps {
  contractId?: string;
  periodStart: Date;
  periodEnd: Date;
  extendPeriod?: boolean;
  showOnlyRequests?: boolean;
  roleFilters?: RoleFilterInput;
  typeFilters?: TypeFilters;
  state?: string;
  skipQuery?: boolean;
  role: string;
  dashboard?: boolean;
}

type TimesheetRaw = any;
type Grouped<T> = Record<string, T[]>;
const groupBy = <T>(array: T[], selector: (item: T) => string): Grouped<T> =>
  array.reduce((acc, item) => {
    const key = selector(item);
    (acc[key] = acc[key] || []).push(item);
    return acc;
  }, {} as Grouped<T>);

interface CreateRequestInput {
  name: string;
  description?: string | null;
  startTime: string | Date;
  endTime?: string | Date | null;
  projectId?: string | null;
  clientId?: string | null;
  costId?: string | null;
}

interface UpdateRequestInput {
  _id: string;
  name: string;
  description?: string | null;
  startTime: string | Date;
  endTime?: string | Date | null;
  projectId?: string | null;
  clientId?: string | null;
  costId?: string | null;
}

class ClientValidationError extends Error {
  code: string;
  constructor(code: string) {
    super(code);
    this.name = "ClientValidationError";
    this.code = code;
  }
}

export const useMyTimesheetTimesheetList = ({
  contractId = "",
  periodStart,
  periodEnd,
  extendPeriod = false,
  showOnlyRequests = false,
  roleFilters,
  typeFilters,
  state,
  skipQuery = false,
  role,
  dashboard = false,
}: UseTimesheetProps) => {
  const { t, i18n } = useTranslation();
  const { setOpenMessage } = useOcSnackbar();

  const getLocale = useCallback<() => Locale>(() => {
    const map: Record<string, Locale> = { en: enLocale, hu: huLocale };
    return map[i18n.language] || enLocale;
  }, [i18n.language]);

  const variables = useMemo(() => {
    const start = extendPeriod ? subDays(periodStart, 6) : periodStart;
    const end = extendPeriod ? addDays(periodEnd, 6) : periodEnd;
    return { contractId, periodStart: start, periodEnd: end, state, role, roleFilters, typeFilters };
  }, [contractId, periodStart, periodEnd, extendPeriod, state, role, roleFilters, typeFilters]);

  const { data: queryData, loading, error, refetch } = useQuery(MY_TIMESHEET_GET_TIMESHEET_LIST, {
    fetchPolicy: "cache-and-network",
    skip: skipQuery,
    variables,
  });
  const [runListTimesheets, { data: mutationListData }] = useMutation(MY_TIMESHEET_TIMESHEET_LIST, { variables });
  useEffect(() => {
    runListTimesheets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []
  );

  useSubscription(MY_TIMESHEET_TIMESHEETS_SUBSCRIPTION, { onData: () => refetch() });

  const [rawCreate] = useMutation(MY_TIMESHEET_CREATE_TIMESHEET);
  const createMyRequest = useCallback(
    async (input: CreateRequestInput) => {
      const limit = endOfDay(addDays(new Date(), 2));
      const start = toDate(input.startTime);
      const end = toDate(input.endTime);

      if ((start && isAfter(start, limit)) || (end && isAfter(end, limit))) {
        throw new ClientValidationError("OC_DATE_TOO_FAR_AWAY");
      }

      try {
        await rawCreate({
          variables: {
            ...input,
            startTime: toISO(input.startTime)!,
            endTime: input.endTime ? toISO(input.endTime) : null,
            projectId: input.projectId ?? null,
            clientId: input.clientId ?? null,
            costId: input.costId ?? null,
          },
        });
        refetch();
        setOpenMessage({ type: "success", message: t("MY_TIMESHEET_SUCCESSFUL_CREATED") });
      } catch {
        setOpenMessage({ type: "error", message: t("MY_TIMESHEET_CREATE_FAILED") });
      }
    },
    [rawCreate, refetch, setOpenMessage, t]
  );

  const [updateRaw] = useMutation(MY_TIMESHEET_UPDATE_TIMESHEET);
  const updateMyRequest = useCallback(
    async (input: UpdateRequestInput) => {
      const limit = endOfDay(addDays(new Date(), 2));
      const start = toDate(input.startTime);
      const end = toDate(input.endTime);

      if ((start && isAfter(start, limit)) || (end && isAfter(end, limit))) {
        setOpenMessage({ type: "error", message: t("OC_DATE_TOO_FAR_AWAY") });
        return;
      }

      try {
        await updateRaw({
          variables: {
            _id: input._id,
            name: input.name,
            description: input.description ?? null,
            startTime: toISO(input.startTime)!,
            endTime: input.endTime ? toISO(input.endTime) : null,
            projectId: input.projectId ?? null,
            clientId: input.clientId ?? null,
            costId: input.costId ?? null,
          },
        });
        refetch();
        setOpenMessage({ type: "success", message: t("MY_TIMESHEET_SUCCESSFUL_UPDATED") });
      } catch {
        setOpenMessage({ type: "error", message: t("MY_TIMESHEET_UPDATE_FAILED") });
      }
    },
    [updateRaw, refetch, setOpenMessage, t]
  );
  const [removeRaw] = useMutation(MY_TIMESHEET_REMOVE_TIMESHEET);
  const removeMyRequest = useCallback(
    async (_id: string) => {
      try {
        await removeRaw({ variables: { _id } });
        refetch();
        setOpenMessage({ type: "success", message: t("MY_TIMESHEET_SUCCESSFUL_REMOVED") });
      } catch {
        setOpenMessage({ type: "error", message: t("MY_TIMESHEET_REMOVAL_FAILED") });
      }
    },
    [removeRaw, refetch, setOpenMessage, t]
  );

  const baseData: TimesheetRaw[] = useMemo(
    () => (skipQuery ? mutationListData?.listMysheetTimesheets : queryData?.listMysheetTimesheets) || [],
    [skipQuery, queryData, mutationListData]
  );

  const filteredBase = useMemo(
    () =>
      baseData.filter((item) => {
        const date = parseISO(item.startTime);
        return (
          (isAfter(date, periodStart) || +date === +periodStart) &&
          (isBefore(date, periodEnd) || +date === +periodEnd) &&
          !item._id.startsWith("leave-")
        );
      }),
    [baseData, periodStart, periodEnd]
  );

  const filteredRequests = useMemo(
    () => filteredBase.filter((item) => item.state === "requested"),
    [filteredBase]
  );
  const stateFiltered = showOnlyRequests ? filteredRequests : filteredBase;

  const extendedData = useMemo(() => {
    const locale = getLocale();
    return stateFiltered
      .filter((item) => !dashboard || (item.state !== "requested" && item.state !== "rejected"))
      .map((item) => {
        const start = parseISO(item.startTime);
        const end = item.endTime ? parseISO(item.endTime) : null;
        const duration = end
          ? (() => {
            const diff = differenceInMinutes(end, start);
            const hrs = Math.floor(diff / 60);
            const mins = diff % 60;
            return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
          })()
          : "00:00";
        const weekNumber = getWeek(start);
        const rawDate = format(start, "yyyy.MM.dd", { locale });
        const rawEndDate = end ? format(end, "yyyy.MM.dd", { locale }) : null;
        const [monthDay, dayName] = format(start, "MMMM dd. - EEEE", { locale }).split(" - ");
        const date = `${monthDay.charAt(0).toUpperCase() + monthDay.slice(1)} - ${dayName.charAt(0).toUpperCase() + dayName.slice(1)}`;
        const messageCount = (item.messageCount || 0) + (item.description ? 1 : 0);
        const createdAtFormatted = item.createdAt
          ? new Date(item.createdAt).toLocaleDateString("hu-HU", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })
          : "";
        return {
          ...item,
          startTime: format(start, "HH:mm", { locale }),
          endTime: end ? format(end, "HH:mm", { locale }) : "",
          duration,
          weekNumber,
          rawDate,
          rawEndDate,
          date,
          messageCount,
          createdAt: createdAtFormatted,
        };
      });
  }, [stateFiltered, dashboard, getLocale]);

  const parsedData = useMemo(() => {
    const byWeek = groupBy(extendedData, (x) => String(x.weekNumber));
    return Object.entries(byWeek)
      .map(([week, items]) => {
        const byDate = groupBy(items, (i) => i.date);
        const timesheets = Object.entries(byDate).map(([date, list]) => ({
          date,
          dailySum: sumTimeStrings(list.map((x) => x.duration)),
          timesheets: list,
        }));
        const weeklySum = sumTimeStrings(timesheets.map((x) => x.dailySum));
        return { weekNumber: week, weeklySum, timesheets };
      })
      .sort((a, b) => Number(b.weekNumber) - Number(a.weekNumber));
  }, [extendedData]);

  const events = useMemo(
    () =>
      baseData.map((item) => ({
        id: item._id,
        title: item.name,
        clientName: item.clientName,
        projectName: item.projectName,
        costName: item.costName,
        start: new Date(item.startTime),
        end: item.endTime ? new Date(item.endTime) : null,
        state: item.state,
      })),
    [baseData],
  );

  const summaryByType = (type: string) => {
    return _?.chain(extendedData)
      ?.groupBy(type)
      ?.map((value, key) => {
        let sum = "00:00";
        let cost = 0;
        let color = "";
        const sums = value?.map((timesheet) => {
          const typeString = type.substring(0, type.length - 4);
          cost = cost + timesheet.cost;
          color = timesheet?.[`${typeString}Color`];
          if (!timesheet?.duration) {
            return "00:00";
          }
          return timesheet?.duration;
        });
        sum = sumTimeStrings(sums);
        if (!color) {
          color = "#000000";
        }
        return {
          name: key === "null" ? t("OC_NOT_PROVIDED") : key,
          summaryTime: sum,
          summaryCost: cost,
          color,
          _id: uuid(),
        };
      })
      ?.value();
  };

  const hoursSummary = useMemo(
    () => sumTimeStrings(extendedData.map((x) => x.duration || "00:00")),
    [extendedData],
  );

  const costsSummary = useMemo(
    () => extendedData.reduce((acc, x) => acc + (x.cost || 0), 0),
    [extendedData],
  );

  const hoursSummaryBillable = useMemo(
    () =>
      sumTimeStrings(
        extendedData
          .filter((x) => x.costBillable)
          .map((x) => x.duration || "00:00"),
      ),
    [extendedData],
  );

  return {
    data: parsedData,
    events,
    loading,
    error,
    refetch,
    getRequest: useCallback(
      (id: string) => extendedData.find((x) => x._id === id),
      [extendedData],
    ),
    createMyRequest,
    updateMyRequest,
    removeMyRequest,
    openedItem: useMemo(() => baseData.find((x) => !x.endTime), [baseData]),
    dailySums: useMemo(
      () =>
        sumTimeStrings(
          extendedData
            .filter(
              (x) =>
                format(new Date(), "yyyy.MM.dd") === x.rawDate && x.endTime,
            )
            .map((x) => x.duration),
        ),
      [extendedData],
    ),
    summaryByType,
    hoursSummary,
    costsSummary,
    hoursSummaryBillable,
    extendedData,
    runQuery: refetch,
    runQueryVariables: variables,
  };
};

export default useMyTimesheetTimesheetList;
